import { executeRequest } from "@/common/utils/http/httpClient";
import { getCurrentEnvironmentVariables } from "@/lib/CONSTANTS";
import { endpointMapper } from "@/common/service/endpoint.map.js";
import { replaceAllMatchPattern } from "@/common/utils/generic/commonHelper.js";

const envSpecificApiDomain = getCurrentEnvironmentVariables().apiDomain;

const subPath = "v1";
const subPathV2 = "v2";

const notificationApiPath = `${replaceAllMatchPattern(
  envSpecificApiDomain[subPath],
  endpointMapper[subPath].notification
)}`;
const rulesApiPathV2 = `${replaceAllMatchPattern(envSpecificApiDomain[subPathV2], endpointMapper[subPathV2].rules)}`;

// -------------------------------------------------- Notification Service -----------------------------------------
/**
 * getNotificationList
 * @description: Function which will trigger async call to get Notification List
 *              based on queryParams to trigger this API
 * @returns Array of notification Object
 */

const getNotificationList = async (queryParams, rawResponse = false) => {
  const {
    countryCode,
    fromDate,
    page,
    size,
    toDate,
    sortBy,
    fetchType,
    sortByDirection,
    notificationCodes,
    manifestType,
    search
  } = queryParams;
  let url = `${notificationApiPath}/notification/list?countryCode=${countryCode}&notificationCodes=${notificationCodes}&fromDate=${fromDate}&toDate=${toDate}&size=${size}&page=${page}&sortBy=${sortBy}&fetchType=${fetchType}&manifestType=${manifestType}&sortByDirection=${sortByDirection}`;
  url = search ? url + `&search=${search}` : url;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * notification Count
 * @description: Function which will trigger async call to get Notification count
 *              based on queryParams to trigger this API
 * @returns Array of notification Object
 */

const getNotificationCount = async (queryParams, rawResponse = false) => {
  const { countryCode, fetchType, manifestType, fromDate, toDate, notificationCodes, search } = queryParams;
  let url = `${notificationApiPath}/notification/count?countryCode=${countryCode}&notificationCodes=${notificationCodes}&fetchType=${fetchType}&manifestType=${manifestType}&fromDate=${fromDate}&toDate=${toDate}`;
  url = search ? url + `&search=${search}` : url;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getNotificationDetails
 * @description: Function which will trigger async call to get Notification Details
 *              based on queryParams to trigger this API
 * @returns notification Object
 */

const getNotificationDetails = async (notificationId, rawResponse = false) => {
  const url = `${notificationApiPath}/notification/${notificationId}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getNAMNotificationDetails
 * @description: Function which will trigger async call to get Notification Details for US and CA
 *              based on queryParams to trigger this API
 * @returns notification Object
 */

const getNAMNotificationDetails = async (notificationId, rawResponse = false) => {
  const url = `${notificationApiPath}/notification/statusnotification/${notificationId}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * getFreeformTypeList
 * @description: Function which will trigger async call to get Notification Codes
 *              based on queryParams to trigger this API
 * @returns notification Object
 */

const getFreeformTypeList = async (params, rawResponse = false) => {
  const { countryCode, isNew } = params;
  const url = `${notificationApiPath}/notification/rules?countryCode=${countryCode}&isNew=${isNew}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * updateNotificationSubmissionStatus
 * @description:Function which will trigger async call to the data for updating vessel reference
 * @returns :A
 * @body : bolNumber and cargoDestination as input which contains references object
 **/

const updateNotificationSubmissionStatus = async (params, rawResponse = false) => {
  const { ediTransmissionId, sequenceNumber, isSent } = params;
  const url = `${notificationApiPath}/notification/submissionstatus/update?ediTransmissionId=${ediTransmissionId}&sequenceNumber=${sequenceNumber}&isSent=${isSent}`;
  return executeRequest({
    url,
    method: "PUT",
    rawResponse
  });
};

/**
 * sendNewNotification
 * @description:Function which will trigger async call for sending free form Notification
 * @returns : Object
 * @body :req_body
 **/

const sendNewNotification = async (req_body, rawResponse = false) => {
  const url = `${notificationApiPath}/notification/conversation`;
  return executeRequest({
    url,
    method: "POST",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * fileUploadNotification
 * @description:Function which will trigger async call for sending free form Notification upload files
 * @returns : Object
 * @body :req_body
 **/

const fileUploadNotification = async (formData, rawResponse = false) => {
  const url = `${notificationApiPath}/notification/file/upload`;
  return executeRequest({
    url,
    method: "POST",
    body: formData,
    rawResponse,
    headerUpload: true
  });
};

/**
 * saveNotificationDetails
 * @description:Function which will trigger async call for saving Notification Details
 * @returns : Object
 * @body :req_body
 **/

const saveNotificationDetails = async (req_body, rawResponse = false) => {
  const url = `${notificationApiPath}/notification/conversation`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * getNotificationComplete
 * @description: Function which will trigger async call to get respective bol data
 *              based on the bol number to trigger this API
 * @returns Array of bol data object
 * @param: {*}
 */

const markNotificationCompleted = async (params, rawResponse = false) => {
  const { notificationId, req_body } = params;
  const url = `${notificationApiPath}/notification/complete/${notificationId}`;
  return executeRequest({
    url,
    method: "PUT",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

/**
 * getAuditNotificationDetails
 * @description: Function which will trigger async call to get Notification details
 *               trigger this API
 * @returns Array of category object
 */

const getAuditNotificationDetails = async (ediTransmissionId, rawResponse = false) => {
  const url = `${notificationApiPath}/notification/meta-data/${ediTransmissionId}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

/**
 * templateProcess
 * @description: Function which will trigger the template operations like insert,edit, deactivate, activate template
 *               trigger this API
 * @returns Template object
 */

const templateProcess = async ({ path, params, req_body, rawResponse = false }) => {
  const paramsPath = generateParamsPath(params);
  const url = paramsPath ? `${notificationApiPath}/${path}?${paramsPath}` : `${notificationApiPath}/${path}`;

  return executeRequest({
    url,
    method: "POST",
    body: JSON.stringify(req_body),
    rawResponse
  });
};

// -------------------------------------------------- Rules Service (V2) -----------------------------------------

/**
 * getAttachmentTypeList
 * @description: Function which will trigger async call to get Notification Codes
 *              based on queryParams to trigger this API
 * @returns notification Object
 */

const getAttachmentTypeList = async (params, rawResponse = false) => {
  const { countryCode, notificationCode, fetchAttachmentTypes } = params;
  const url = `${rulesApiPathV2}/notificationRule?countryCode=${countryCode}&notificationCode=${notificationCode}&fetchAttachmentTypes=${fetchAttachmentTypes}`;
  return executeRequest({
    url,
    method: "GET",
    rawResponse
  });
};

// -------------------------------------------------- Utility Methods -----------------------------------------

/**
 * generateParamsPath
 * @description: Function which will accept params object and create query params url
 *
 * @returns query params url string
 */
const generateParamsPath = params => {
  if (params) {
    const parts = [];
    for (const [key, value] of Object.entries(params)) {
      if (value != null && value !== "") {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }
    return parts.join("&");
  }
};

export {
  getNotificationCount,
  markNotificationCompleted,
  saveNotificationDetails,
  updateNotificationSubmissionStatus,
  getNotificationDetails,
  getAuditNotificationDetails,
  getNAMNotificationDetails,
  fileUploadNotification,
  getAttachmentTypeList,
  sendNewNotification,
  getFreeformTypeList,
  getNotificationList,
  templateProcess
};
